import React, { useState, useEffect } from 'react';
import { theme, alpha } from '../theme';

interface CustomDatePickerProps {
  selectedDate: Date | null;
  onChange: (date: Date | null) => void;
  minDate: Date;
  onClose: () => void;
  isMobile?: boolean;
}

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  selectedDate,
  onChange,
  minDate,
  onClose,
  isMobile = false
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  // Nombres de los meses en español
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Nombres de los días de la semana en español
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  // Obtener el primer día del mes y el número de días
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const lastDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();

  // Generar array de días del mes
  const days = [];
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  // Generar días del mes anterior para completar la primera semana
  const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0);
  const prevMonthDays = [];
  for (let i = startingDayOfWeek - 1; i >= 0; i--) {
    prevMonthDays.push(prevMonth.getDate() - i);
  }

  // Generar días del mes siguiente para completar la última semana
  const nextMonthDays = [];
  const totalCells = Math.ceil((startingDayOfWeek + daysInMonth) / 7) * 7;
  const remainingCells = totalCells - (startingDayOfWeek + daysInMonth);
  for (let i = 1; i <= remainingCells; i++) {
    nextMonthDays.push(i);
  }

  const handleDateClick = (day: number) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (clickedDate >= minDate) {
      onChange(clickedDate);
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const isDateSelected = (day: number) => {
    if (!selectedDate) return false;
    return selectedDate.getDate() === day &&
           selectedDate.getMonth() === currentMonth.getMonth() &&
           selectedDate.getFullYear() === currentMonth.getFullYear();
  };

  const isDateDisabled = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date < minDate;
  };

  const isDateHovered = (day: number) => {
    if (!hoveredDate) return false;
    return hoveredDate.getDate() === day &&
           hoveredDate.getMonth() === currentMonth.getMonth() &&
           hoveredDate.getFullYear() === currentMonth.getFullYear();
  };

  return (
    <div style={{
      backgroundColor: 'transparent',
      width: '100%',
      fontFamily: theme.fonts.body
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.sm,
        paddingBottom: theme.spacing.xs,
        borderBottom: `1px solid ${theme.colors.border}`
      }}>
        <button
          onClick={handlePrevMonth}
          style={{
            background: 'none',
            border: 'none',
            color: theme.colors.primary,
            fontSize: '18px',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: theme.borderRadius.button
          }}
        >
          ‹
        </button>
        
        <h3 style={{
          margin: 0,
          color: theme.colors.text,
          fontSize: theme.fonts.sizeBody,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: theme.fonts.letterSpacingWide
        }}>
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        
        <button
          onClick={handleNextMonth}
          style={{
            background: 'none',
            border: 'none',
            color: theme.colors.primary,
            fontSize: '18px',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: theme.borderRadius.button
          }}
        >
          ›
        </button>
      </div>

      {/* Días de la semana */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '4px',
        marginBottom: theme.spacing.sm
      }}>
        {dayNames.map((dayName) => (
          <div
            key={dayName}
            style={{
              textAlign: 'center',
              padding: '8px 4px',
              color: theme.colors.textSecondary,
              fontSize: theme.fonts.sizeBody,
              fontWeight: 600
            }}
          >
            {dayName}
          </div>
        ))}
      </div>

      {/* Días del calendario */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '4px'
      }}>
        {/* Días del mes anterior */}
        {prevMonthDays.map((day) => (
          <div
            key={`prev-${day}`}
            style={{
              aspectRatio: '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.colors.textSecondary,
              opacity: 0.3,
              fontSize: theme.fonts.sizeBody,
              minHeight: isMobile ? '52px' : '48px',
              minWidth: isMobile ? '52px' : '48px'
            }}
          >
            {day}
          </div>
        ))}

        {/* Días del mes actual */}
        {days.map((day) => {
          const isSelected = isDateSelected(day);
          const isDisabled = isDateDisabled(day);
          const isHovered = isDateHovered(day);
          
          return (
            <button
              key={day}
              onClick={() => handleDateClick(day)}
              disabled={isDisabled}
              onMouseEnter={() => setHoveredDate(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day))}
              onMouseLeave={() => setHoveredDate(null)}
              style={{
                aspectRatio: '1',
                border: 'none',
                borderRadius: theme.borderRadius.button,
                backgroundColor: isSelected 
                  ? theme.colors.primary 
                  : isHovered 
                    ? `${alpha('primary', '20')}`
                    : 'transparent',
                color: isSelected 
                  ? theme.colors.white 
                  : isDisabled 
                    ? theme.colors.textSecondary 
                    : theme.colors.text,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                fontSize: theme.fonts.sizeBody,
                fontWeight: isSelected ? 600 : 400,
                opacity: isDisabled ? 0.5 : 1,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: isMobile ? '52px' : '48px',
                minWidth: isMobile ? '52px' : '48px'
              }}
            >
              {day}
            </button>
          );
        })}

        {/* Días del mes siguiente */}
        {nextMonthDays.map((day) => (
          <div
            key={`next-${day}`}
            style={{
              aspectRatio: '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.colors.textSecondary,
              opacity: 0.3,
              fontSize: theme.fonts.sizeBody,
              minHeight: isMobile ? '52px' : '48px',
              minWidth: isMobile ? '52px' : '48px'
            }}
          >
            {day}
          </div>
        ))}
      </div>
    </div>
  );
};
